import { z } from 'zod';
import { supabase } from '@/shared/supabase/client';
import { compresserPhoto } from '@/shared/hors-ligne/compression';
import { COMPARTIMENT_PHOTOS, verifierIdentite } from '@/shared/comptes/photo';
import type { DepotComptes } from '../types';
import {
  SELECTION_COMPTE,
  ligneCompte,
  messageDeReponse,
  messageErreur,
  versUtilisateur,
} from './lignes';

const lignesId = z.array(z.object({ id: z.string() }));

/* `profils_bureau_sans_partenaire` : la base refuse un compte du bureau rattaché. */
const RATTACHEMENT = {
  '23514': 'Un compte du bureau ne peut pas être rattaché à un partenaire.',
  '23503': 'Ce partenaire n’existe plus.',
};

async function mettreAJour(
  compteId: string,
  valeurs: Record<string, unknown>,
  defaut: string,
  propres: Readonly<Record<string, string>> = {}
): Promise<void> {
  const { data, error } = await supabase()
    .from('profils')
    .update(valeurs)
    .eq('id', compteId)
    .select('id');
  if (error) throw new Error(messageErreur(error, defaut, propres));
  if (lignesId.parse(data).length === 0) throw new Error('Ce compte n’existe plus.');
}

export const comptesSupabase: DepotComptes = {
  async lister() {
    const { data, error } = await supabase()
      .from('profils')
      .select(SELECTION_COMPTE)
      .order('email');
    if (error) throw new Error(messageErreur(error, 'Les comptes n’ont pas pu être lus.'));
    return z.array(ligneCompte).parse(data).map(versUtilisateur);
  },

  async rattacher(compteId, role, partenaireId) {
    const valeurs =
      role === 'bureau'
        ? { role: 'bureau', partenaire_id: null }
        : { role: 'partenaire', partenaire_id: partenaireId };
    await mettreAJour(compteId, valeurs, 'Le compte n’a pas pu être rattaché.', RATTACHEMENT);
  },

  async modifierIdentite(compteId, identite) {
    const propre = verifierIdentite(identite);
    await mettreAJour(
      compteId,
      { prenom: propre.prenom, nom_famille: propre.nomFamille },
      'Le nom n’a pas pu être enregistré.'
    );
  },

  async changerPhoto(compteId, fichier) {
    const photos = supabase().storage.from(COMPARTIMENT_PHOTOS);
    const avant = await supabase().from('profils').select('photo').eq('id', compteId).maybeSingle();
    const ancienne = z.object({ photo: z.string().nullable() }).nullable().parse(avant.data)?.photo;

    let chemin: string | null = null;
    if (fichier) {
      const reduite = await compresserPhoto(fichier, 512);
      if (reduite.size > 2 * 1024 * 1024) throw new Error('La photo dépasse 2 Mo.');
      chemin = `${compteId}/${String(Date.now())}.jpg`;
      const depot = await photos.upload(chemin, reduite, {
        contentType: reduite.type || 'image/jpeg',
        upsert: false,
      });
      if (depot.error) throw new Error('La photo n’a pas pu être envoyée.');
    }
    try {
      await mettreAJour(compteId, { photo: chemin }, 'La photo n’a pas pu être enregistrée.');
    } catch (e) {
      if (chemin) await photos.remove([chemin]);
      throw e;
    }
    /* L'ancienne ne sert plus ; un échec ici laisse un fichier orphelin, sans plus. */
    if (ancienne) await photos.remove([ancienne]);
  },

  async inviterBureau(invitation) {
    /* Comme pour un partenaire : la clé de service reste sur le serveur
       (functions/api/inviter.ts), qui vérifie que l'appelant est du bureau. */
    const { data } = await supabase().auth.getSession();
    const jeton = data.session?.access_token;
    if (!jeton) throw new Error('Votre session a expiré : reconnectez-vous.');
    let reponse: Response;
    try {
      reponse = await fetch('/api/inviter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
        body: JSON.stringify({
          role: 'bureau',
          email: invitation.email.trim(),
          prenom: invitation.prenom.trim(),
          nomFamille: invitation.nomFamille.trim(),
          niveau: invitation.niveau,
        }),
      });
    } catch {
      throw new Error('Le service d’invitation est injoignable. Vérifiez la connexion.');
    }
    if (reponse.ok) return;
    let corps: unknown = null;
    try {
      corps = await reponse.json();
    } catch {
      /* Pas de JSON : message par défaut. */
    }
    throw new Error(messageDeReponse(corps) ?? 'L’invitation n’a pas pu être envoyée.');
  },

  async changerNiveau(compteId, niveau) {
    await mettreAJour(compteId, { niveau }, 'Le niveau n’a pas pu être changé.', {
      '23505': 'Il n’y a qu’un CEO.',
    });
  },

  async definirActif(compteId, actif) {
    await mettreAJour(
      compteId,
      { actif },
      actif ? 'Le compte n’a pas pu être réactivé.' : 'Le compte n’a pas pu être suspendu.'
    );
  },
};
